/**
 * MatchingEditor.jsx
 * Admin content editor for lesson_type === 'matching'
 *
 * Content shape:
 *   { pairs: [{ left, right }], mode: 'two_column' | 'memory' }
 *
 * Props:
 *   value    — current content object
 *   onChange — (newContent) => void
 */


import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DEFAULT_PAIR = { left: '', right: '' };
const MODES = ['two_column', 'memory'];

export default function MatchingEditor({ value, onChange }) {
  const { t } = useLanguage();
  const pairs = Array.isArray(value?.pairs) ? value.pairs : [];
  const mode = value?.mode || 'two_column';

  const update = (next) => onChange({ ...value, ...next });

  const addPair = () => update({ pairs: [...pairs, { ...DEFAULT_PAIR }] });

  const removePair = (idx) => update({ pairs: pairs.filter((_, i) => i !== idx) });

  const updatePair = (idx, field, val) =>
    update({ pairs: pairs.map((p, i) => (i === idx ? { ...p, [field]: val } : p)) });

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...pairs];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    update({ pairs: next });
  };

  const moveDown = (idx) => {
    if (idx === pairs.length - 1) return;
    const next = [...pairs];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    update({ pairs: next });
  };

  return (
    <div className="space-y-3">
      {/* Mode selector */}
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
          {t('lessonEditor.matching.mode')}
        </label>
        <select
          value={mode}
          onChange={(e) => update({ mode: e.target.value })}
          className="w-full sm:w-48 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
        >
          {MODES.map((m) => (
            <option key={m} value={m}>
              {t(`lessonEditor.matching.mode_${m}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Pairs */}
      {pairs.length === 0 && (
        <p className="text-xs text-center text-[var(--text-muted)] py-4 border-2 border-dashed border-[var(--border-light)] rounded-xl">
          {t('lessonEditor.common.emptyList')}
        </p>
      )}
      {pairs.map((pair, idx) => (
        <div
          key={idx}
          className="bg-[var(--bg-app)] border-2 border-[var(--border-light)] rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-[var(--text-muted)]">#{idx + 1}</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                aria-label={t('lessonEditor.common.moveUp')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === pairs.length - 1}
                aria-label={t('lessonEditor.common.moveDown')}
                className="p-1 rounded hover:bg-white disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removePair(idx)}
                aria-label={t('lessonEditor.matching.removePair')}
                className="p-1 rounded hover:bg-red-50 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.matching.left')} *
              </label>
              <Input
                value={pair.left}
                onChange={(e) => updatePair(idx, 'left', e.target.value)}
                placeholder={t('lessonEditor.matching.leftPlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                {t('lessonEditor.matching.right')} *
              </label>
              <Input
                value={pair.right}
                onChange={(e) => updatePair(idx, 'right', e.target.value)}
                placeholder={t('lessonEditor.matching.rightPlaceholder')}
                className="rounded-xl border-2 border-[var(--border-light)] text-sm"
              />
            </div>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addPair}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('lessonEditor.matching.addPair')}
      </Button>
    </div>
  );
}
